import { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { AppModal } from "global/components/AppModal";
import notifiers from "global/constants/NotificationConstants";
import { openErrorNotification, shortenFileName } from "helpers/methods";
import { downloadSignerAttachment } from "../ParticipantScreenService";
import t from "../SignerAttachments.tokens";
import { attachmentFileKind, SignerAttachmentItem } from "./AttachmentPolicy";

const { palette: p } = t;

interface AttachmentPreviewDialogProps {
  open: boolean;
  item: SignerAttachmentItem | null;
  /** Needed to fetch a file that was uploaded in an earlier session and has no local File. */
  requestToken?: string;
  onClose: () => void;
}

const AttachmentPreviewDialog = ({
  open,
  item,
  requestToken,
  onClose,
}: AttachmentPreviewDialogProps) => {
  const [objectUrl, setObjectUrl] = useState<string>("");
  const [failed, setFailed] = useState<boolean>(false);

  const kind = useMemo(
    () => (item ? attachmentFileKind(item.name) : "other"),
    [item],
  );
  const isImage = kind === "image";
  const isPdf = kind === "pdf";

  useEffect(() => {
    let revoke: string | null = null;
    let cancelled = false;

    if (!open || !item) {
      setObjectUrl("");
      setFailed(false);
      return undefined;
    }

    setFailed(false);

    if (item.file) {
      revoke = URL.createObjectURL(item.file);
      setObjectUrl(revoke);
    } else if (requestToken) {
      setObjectUrl("");
      (async () => {
        try {
          const blob = await downloadSignerAttachment(requestToken, item.id);
          if (cancelled) return;
          revoke = URL.createObjectURL(blob);
          setObjectUrl(revoke);
        } catch {
          if (!cancelled) {
            setFailed(true);
            openErrorNotification(notifiers.ATTACHMENT_DOWNLOAD_FAILED);
          }
        }
      })();
    } else {
      setObjectUrl("");
      setFailed(true);
    }

    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [open, item, requestToken]);

  if (!item) return null;

  return (
    // Chrome deliberately matches the app's other file preview — TicketAttachments: neutral tone, the
    // widest size step, its own scroller, and a single Close action. Viewing a file is not a place for
    // choices; downloading already lives on the attachment row and on the sender's card.
    <AppModal
      open={open}
      onClose={onClose}
      // Trimmed the same way as the row it was opened from, so a long name cannot push the modal
      // header out of shape. The iframe/img still carries the full name for assistive tech.
      title={shortenFileName(item.name, 48)}
      tone="neutral"
      // The widest step, same as the ticket viewer — a document needs the room.
      size="xl"
      hideIcon
      scrollBody={false}
      primaryLabel="Close"
      onPrimary={onClose}
      hideCancel
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: t.preview.viewerHeight,
        }}
      >
        {/* The file itself, not a mock-up. An earlier version drew grey placeholder lines here for
            anything that was not an image, so every PDF opened to an empty-looking sheet. */}
        {failed ? (
          <Typography
            role="alert"
            sx={{
              fontSize: t.preview.metaFontSize,
              color: p.muted,
              textAlign: "center",
            }}
          >
            This file could not be loaded. Please close and try again.
          </Typography>
        ) : !objectUrl ? (
          <CircularProgress size={28} aria-label={`Loading ${item.name}`} />
        ) : isImage ? (
          <Box
            component="img"
            src={objectUrl}
            alt={item.name}
            onError={() => setFailed(true)}
            sx={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              borderRadius: t.preview.imageRadius,
              border: t.preview.sheetBorder,
              backgroundColor: p.white,
            }}
          />
        ) : isPdf ? (
          // Same iframe treatment as TicketAttachments, including `#toolbar=0&view=fitH` so the
          // browser's viewer opens fitted and without its own toolbar competing with the dialog.
          // No new dependency: react-pdf exists in this app but nothing is code-split, so importing
          // it here would add weight to every route.
          <Box
            component="iframe"
            title="Attachment preview"
            src={`${objectUrl}#toolbar=0&view=fitH`}
            scrolling="yes"
            sx={{ width: "100%", height: "100%", border: "none" }}
          />
        ) : (
          <Typography
            sx={{
              fontSize: t.preview.metaFontSize,
              color: p.muted,
              textAlign: "center",
            }}
          >
            This file type cannot be previewed.
          </Typography>
        )}
      </Box>
    </AppModal>
  );
};

export default AttachmentPreviewDialog;
